import React from 'react';
import { Card } from '../components/ui/card';
import { CheckCircle, Target, Award, TrendingUp } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-cream">
      <Header />
      
      {/* Page Header */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-burgundy to-burgundy-dark text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            À propos de Volaille Prime
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Découvrez notre histoire, nos valeurs et notre engagement envers la qualité
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {/* Who We Are */}
          <div className="max-w-4xl mx-auto mb-16">
            <Card className="p-8 md:p-12 bg-white shadow-xl border-2 border-gray-100">
              <h2 className="text-3xl font-bold text-burgundy mb-6">Qui sommes-nous ?</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  <strong className="text-burgundy">Volaille Prime</strong> est une marque dédiée à la production 
                  et à la commercialisation de poulets de qualité, issus d'un élevage structuré et suivi.
                </p>
                <p>
                  Nous croyons qu'un bon produit commence par :
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-burgundy flex-shrink-0 mt-1" size={20} />
                    <span>Une organisation sérieuse</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-burgundy flex-shrink-0 mt-1" size={20} />
                    <span>Une production maîtrisée</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-burgundy flex-shrink-0 mt-1" size={20} />
                    <span>Une relation de confiance avec nos clients</span>
                  </li>
                </ul>
                <p className="pt-4">
                  Installés à <strong className="text-burgundy">Thiès</strong>, nous servons aussi bien les ménages 
                  que les professionnels de la restauration et de la revente.
                </p>
                <p className="text-lg font-semibold text-burgundy pt-4">
                  Notre ambition est de bâtir une activité durable, fiable et respectée.
                </p>
              </div>
            </Card>
          </div>

          {/* Our Commitments */}
          <div className="max-w-5xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-burgundy mb-8 text-center">Notre engagement</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Commitment 1 */}
              <Card className="p-6 bg-white border-2 border-gray-100 hover:border-burgundy/30 hover:shadow-xl transition-all duration-300 text-center group">
                <div className="mb-4 flex justify-center">
                  <div className="p-4 bg-burgundy/10 rounded-2xl group-hover:bg-burgundy group-hover:scale-110 transition-all duration-300">
                    <Award className="text-burgundy group-hover:text-white transition-colors" size={32} />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Qualité constante</h3>
                <p className="text-gray-600 text-sm">
                  Standards élevés pour chaque produit
                </p>
              </Card>

              {/* Commitment 2 */}
              <Card className="p-6 bg-white border-2 border-gray-100 hover:border-burgundy/30 hover:shadow-xl transition-all duration-300 text-center group">
                <div className="mb-4 flex justify-center">
                  <div className="p-4 bg-burgundy/10 rounded-2xl group-hover:bg-burgundy group-hover:scale-110 transition-all duration-300">
                    <Target className="text-burgundy group-hover:text-white transition-colors" size={32} />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Transparence</h3>
                <p className="text-gray-600 text-sm">
                  Communication claire et honnête
                </p>
              </Card>

              {/* Commitment 3 */}
              <Card className="p-6 bg-white border-2 border-gray-100 hover:border-burgundy/30 hover:shadow-xl transition-all duration-300 text-center group">
                <div className="mb-4 flex justify-center">
                  <div className="p-4 bg-burgundy/10 rounded-2xl group-hover:bg-burgundy group-hover:scale-110 transition-all duration-300">
                    <CheckCircle className="text-burgundy group-hover:text-white transition-colors" size={32} />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Respect des normes</h3>
                <p className="text-gray-600 text-sm">
                  Hygiène et sécurité alimentaire
                </p>
              </Card>

              {/* Commitment 4 */}
              <Card className="p-6 bg-white border-2 border-gray-100 hover:border-burgundy/30 hover:shadow-xl transition-all duration-300 text-center group">
                <div className="mb-4 flex justify-center">
                  <div className="p-4 bg-burgundy/10 rounded-2xl group-hover:bg-burgundy group-hover:scale-110 transition-all duration-300">
                    <TrendingUp className="text-burgundy group-hover:text-white transition-colors" size={32} />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Amélioration continue</h3>
                <p className="text-gray-600 text-sm">
                  Innovation et perfectionnement
                </p>
              </Card>
            </div>
          </div>

          {/* Image Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="relative h-96 rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1731328966800-b677e4fcda8d?w=1200&q=80"
                alt="Production Volaille Prime"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-burgundy/80 via-burgundy/30 to-transparent flex items-end">
                <div className="p-8 text-white">
                  <h3 className="text-2xl font-bold mb-2">Production locale à Thiès</h3>
                  <p className="text-gray-200">
                    Un élevage de proximité pour une fraîcheur garantie
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Legal Mention */}
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 bg-gradient-to-br from-burgundy/5 to-gold/5 border-2 border-burgundy/20">
              <h3 className="text-xl font-bold text-burgundy mb-4 text-center">Mention légale</h3>
              <p className="text-center text-gray-700">
                <strong className="text-burgundy">Volaille Prime</strong> est exploitée par{' '}
                <strong className="text-gold">DMB Consulting</strong> – Entreprise Individuelle
              </p>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;